// @ts-nocheck
import React from 'react';
import { Card, CardHeader, Box, Stack, LinearProgress, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { FeedbackSummary } from './types';

interface FeedbackOverviewCardProps {
  title: string;
  subheader?: string;
  data: FeedbackSummary;
  onFeedbackClick?: (feedbackType: string) => void;
}

export default function FeedbackOverviewCard({ title, subheader, data, onFeedbackClick }: FeedbackOverviewCardProps) {
  const theme = useTheme();
  const other = data.other ?? 0;
  const total = data.good + data.partiallyCorrect + data.fullWrong + data.noFeedback + other;
  const withFeedback = data.good + data.partiallyCorrect + data.fullWrong + other;

  const items = [
    {
      label: 'Good',
      key: 'good',
      value: data.good,
      pct: total > 0 ? (data.good / total) * 100 : 0,
      color: theme.palette.success.main,
      icon: 'mdi:thumb-up',
    },
    {
      label: 'Partially Wrong',
      key: 'partiallyCorrect',
      value: data.partiallyCorrect,
      pct: total > 0 ? (data.partiallyCorrect / total) * 100 : 0,
      color: theme.palette.warning.main,
      icon: 'mdi:thumbs-up-down',
    },
    {
      label: 'Full Wrong',
      key: 'fullWrong',
      value: data.fullWrong,
      pct: total > 0 ? (data.fullWrong / total) * 100 : 0,
      color: theme.palette.error.main,
      icon: 'mdi:thumb-down',
    },
    {
      label: 'No Feedback',
      key: 'noFeedback',
      value: data.noFeedback,
      pct: total > 0 ? (data.noFeedback / total) * 100 : 0,
      color: theme.palette.grey[400],
      icon: 'mdi:comment-remove-outline',
    },
    {
      label: 'Other',
      key: 'other',
      value: other,
      pct: total > 0 ? (other / total) * 100 : 0,
      color: theme.palette.info.main,
      icon: 'mdi:help-circle-outline',
    },
  ];

  return (
    <Card>
      <CardHeader title={title} subheader={subheader || `${withFeedback} of ${total} queries received feedback`} />
      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          {items.map((item) => (
            <Box
              key={item.label}
              onClick={() => onFeedbackClick && item.value > 0 && onFeedbackClick(item.key)}
              sx={{
                cursor: onFeedbackClick && item.value > 0 ? 'pointer' : 'default',
                opacity: item.value === 0 ? 0.5 : 1,
                '&:hover':
                  onFeedbackClick && item.value > 0 ? { bgcolor: alpha(item.color, 0.04), borderRadius: 1 } : {},
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      display: 'flex',
                      borderRadius: 0.75,
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(item.color, 0.12),
                      color: item.color,
                    }}
                  >
                    <Iconify icon={item.icon} width={16} />
                  </Box>
                  <Typography variant="body2">{item.label}</Typography>
                </Stack>
                <Typography variant="subtitle2">
                  {item.value} ({item.pct.toFixed(1)}%)
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={item.pct}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: alpha(item.color, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 1 },
                }}
              />
            </Box>
          ))}
        </Stack>
      </Box>
    </Card>
  );
}
